import { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useSwipeable } from 'react-swipeable'
import { useTheme } from 'styled-components'

import { useStatusBarStyle } from '@/capacitor/hooks/useStatusBarStyle'
import { useLayout } from '@/layout/providers/LayoutProvider'
import { pageTransition, pageVariants } from './ContentPageLayout.animations'
import { AnimatedStContentContainer, StContainer } from './ContentPageLayout.styled'
import { ContentPageLayoutHeader } from './ContentPageLayoutHeader'

import type { ReactNode, RefObject } from 'react'
import type { SwipeEventData } from 'react-swipeable'

type ContentPageLayoutProps = {
  animationkey?: string
  backgroundColor?: string
  children?: ReactNode
  header?: ReactNode
  mainRef?: RefObject<HTMLDivElement>
  mainRefId?: string
  onSwipe?: (event: SwipeEventData) => void
  showHeaderAlways?: boolean
  showScrollbar?: boolean
  noPadding?: boolean
  noScroll?: boolean
  className?: string
}

const ContentPageLayout = ({
  animationkey,
  backgroundColor,
  children,
  header,
  mainRef,
  mainRefId,
  onSwipe,
  showHeaderAlways,
  showScrollbar,
  noPadding,
  noScroll,
  className,
}: ContentPageLayoutProps) => {
  const theme = useTheme()
  const { setContentBackgroundColor } = useLayout()
  const handlers = useSwipeable({
    swipeDuration: 250,
    onSwiped: onSwipe,
  })
  const { pathname } = useLocation()

  useStatusBarStyle(backgroundColor ?? theme.Colors.Background.Light)

  useEffect(() => {
    setContentBackgroundColor?.(backgroundColor)
    return () => setContentBackgroundColor?.(undefined)
  }, [backgroundColor, setContentBackgroundColor])

  return (
    <StContainer backgroundColor={backgroundColor} noPadding={noPadding} className={className} {...handlers}>
      <ContentPageLayoutHeader header={header} showHeaderAlways={showHeaderAlways} />

      <AnimatedStContentContainer
        ref={mainRef}
        key={animationkey ?? pathname}
        id={mainRefId}
        initial='initial'
        animate='in'
        variants={pageVariants}
        transition={pageTransition}
        showScrollbar={showScrollbar}
        noScroll={noScroll}
      >
        {children || <Outlet />}
      </AnimatedStContentContainer>
    </StContainer>
  )
}

export type { ContentPageLayoutProps }
export { ContentPageLayout }
